<template>
  <SlideOver :open="on" @close="close">
    <div class="w-screen max-w-md h-full">
      <form
        @submit.prevent="updateEquipmentRevision"
        class="h-full divide-y divide-gray-200 flex flex-col bg-white shadow-xl"
      >
        <div class="flex-1 h-0 overflow-y-auto">
          <div class="py-6 px-4 bg-light-blue-700 sm:px-6">
            <div class="flex items-center justify-between">
              <h2
                id="slide-over-heading"
                class="text-lg font-medium text-white"
              >
                {{ t("title", { identifier: equipment.data.identifier }) }}
              </h2>
              <div class="ml-3 h-7 flex items-center">
                <button
                  type="button"
                  @click="close()"
                  class="
                    bg-light-blue-700
                    rounded-md
                    focus:outline-none focus:ring-2 focus:ring-white
                  "
                >
                  <XIcon class="h-6 w-6 hover:text-white text-light-blue-200" />
                </button>
              </div>
            </div>
            <div class="mt-1">
              <p class="text-sm text-light-blue-300">
                {{
                  t("description", {
                    user: equipmentRevision.data.user.name,
                  })
                }}
              </p>
            </div>
          </div>
          <div class="flex-1 flex flex-col justify-between">
            <div class="px-4 divide-y divide-gray-200 sm:px-6">
              <div class="space-y-6 pt-6 pb-5">
                <ui-input
                  ref="revised_on"
                  :invalid="'revised_on' in form.errors"
                  :error="form.errors.revised_on"
                  type="date"
                  :label="t('revised_on')"
                  v-model="form.revised_on"
                ></ui-input>

                <ui-textarea
                  rows="5"
                  :invalid="'comments' in form.errors"
                  :error="form.errors.comments"
                  :label="t('comments')"
                  v-model="form.comments"
                ></ui-textarea>
              </div>

              <!-- TODO: Add functionality to delete file, and also to update (change) the file -->
              <div
                class="
                  pt-4
                  mb-6
                  flex flex-col
                  justify-stretch
                  space-y-3
                  sm:flex-row sm:space-y-0 sm:space-x-4
                "
              >
                <ui-button
                  focusColorStyling="focus:ring-red-500"
                  backgroundStyling="bg-white hover:bg-red-50"
                  borderStyling="border border-red-300"
                  textColorStyling="text-red-700 hover:text-red-500"
                  type="button"
                  class="flex justify-center w-full"
                  @click="confirmingEquipmentRevisionDeletion = true"
                >
                  <TrashIcon class="-ml-1 mr-2 h-5 w-5 text-red-400" />

                  {{ t("delete_equipment_revision") }}</ui-button
                >
              </div>
            </div>
          </div>
        </div>
        <div class="flex-shrink-0 px-4 py-4 flex justify-end">
          <ui-button
            backgroundStyling="bg-white"
            borderStyling="border border-gray-300"
            textColorStyling="text-gray-700 hover:text-gray-500"
            type="button"
            @click="close"
            >{{ t("cancel") }}</ui-button
          >

          <ui-button :disabled="form.processing" type="submit" class="ml-4">
            {{ t("save") }}
          </ui-button>
        </div>
      </form>
    </div>
  </SlideOver>

  <ui-modal
    :show="confirmingEquipmentRevisionDeletion"
    @close="confirmingEquipmentRevisionDeletion = false"
  >
    <div class="sm:flex sm:items-start">
      <div
        class="
          mx-auto
          flex-shrink-0 flex
          items-center
          justify-center
          h-12
          w-12
          rounded-full
          bg-red-100
          sm:mx-0 sm:h-10 sm:w-10
        "
      >
        <ExclamationIcon class="h-6 w-6 text-red-600" />
      </div>
      <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
        <h3 class="text-lg leading-6 font-medium text-gray-900">
          {{ t("confirm_equipment_revision_deletion_title") }}
        </h3>

        <div class="mt-2">
          <p class="text-sm text-gray-500">
            {{ t("confirm_equipment_revision_deletion_text") }}
          </p>
        </div>
      </div>
    </div>
    <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
      <ui-button
        backgroundStyling="bg-red-600 hover:bg-red-700"
        focusColorStyling="focus:ring-red-500"
        type="button"
        :disabled="form.processing"
        @click="deleteEquipmentRevision"
        class="flex justify-center w-full sm:w-auto ml-0 sm:ml-3"
        >{{ t("delete_equipment_revision") }}</ui-button
      >
      <ui-button
        backgroundStyling="bg-white"
        borderStyling="border border-gray-300"
        textColorStyling="text-gray-700 hover:text-gray-500"
        type="button"
        @click="confirmingEquipmentRevisionDeletion = false"
        class="flex justify-center w-full sm:w-auto mt-3 sm:mt-0"
        >{{ t("nevermind") }}</ui-button
      >
    </div>
  </ui-modal>
</template>

<script>
import { ExclamationIcon, XIcon } from "@heroicons/vue/outline";
import { TrashIcon } from "@heroicons/vue/solid";
import { useI18n } from "vue-i18n";
import SlideOver from "@/Shared/SlideOver";

export default {
  emits: ["toggleVisibility"],

  components: {
    ExclamationIcon,
    XIcon,
    TrashIcon,
    SlideOver,
  },

  props: {
    on: { type: Boolean, required: true },

    equipmentRevision: { type: Object, required: true },

    equipment: { type: Object, required: true },
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  mounted() {
    if (this.on) {
      setTimeout(() => {
        this.$refs.revised_on.$refs.input.focus();
      }, 250);
    }
  },

  data() {
    return {
      confirmingEquipmentRevisionDeletion: false,
      form: this.$inertia.form({
        _method: "PUT",
        comments: this.equipmentRevision.data.comments,
        revised_on: this.equipmentRevision.data.revised_on,
      }),
    };
  },

  methods: {
    updateEquipmentRevision() {
      this.form.put(
        this.route("equipment-revisions.update", {
          equipment_revision: this.equipmentRevision.data.id,
          search: this.$page.props.filters.search,
        }),
        {
          preserveScroll: true,
        }
      );
    },

    deleteEquipmentRevision() {
      this.form.delete(
        this.route("equipment-revisions.destroy", {
          equipment_revision: this.equipmentRevision.data.id,
          search: this.$page.props.filters.search,
        }),
        {
          preserveScroll: true,
          onSuccess: (page) => {
            this.confirmingEquipmentRevisionDeletion = false;
          },
        }
      );
    },

    close() {
      this.form.reset();
      this.form.clearErrors();
      this.$emit("toggleVisibility");
    },
  },

  watch: {
    on: {
      handler: (on) => {
        if (on) {
          setTimeout(() => {
            this.$refs.revised_on.$refs.input.focus();
          }, 250);
        }
      },
    },
    equipmentRevision: {
      handler: (equipmentRevision, oldEquipmentRevision) => {
        if (equipmentRevision.id != oldEquipmentRevision.id) {
          this.form.clearErrors();
          this.form.revised_on = this.equipmentRevision.data.revised_on;
          this.form.comments = this.equipmentRevision.data.comments;
        }
      },
    },
  },
};
</script>


<i18n>
{
  "es": {
    "title": "Verificación de equipo {identifier}",
    "description": "Registrada por {user}",
    "comments": "Comentarios",
    "revised_on": "Fecha de verificación",
    "save": "Guardar",
    "cancel": "Cancelar",
    "confirm_equipment_revision_deletion_title": "¿Seguro que deseas eleminar?",
    "confirm_equipment_revision_deletion_text": "Al eliminar la verificación, no se podrá consultar la información más tarde.",
    "delete_equipment_revision": "Eliminar verificación",
    "nevermind": "Mejor no"
  }
}
</i18n>
